// base plugin - enforces implementation
class DevToolPlugin {
	constructor() {
		if (new.target === DevToolPlugin) {
			throw new Error(
				"DevToolPlugin is abstract; extend it instead of instantiating directly."
			);
		}
		if (
			this.activate === DevToolPlugin.prototype.activate ||
			this.deactivate === DevToolPlugin.prototype.deactivate
		) {
			throw new Error(
				"Plugins must implement activate() and deactivate() methods."
			);
		}
		this.active = false;
	}

	// default implementations just throw so subclasses *must* override
	activate() {
		throw new Error("activate() must be implemented by plugin");
	}

	deactivate() {
		throw new Error("deactivate() must be implemented by plugin");
	}

	// convenience toggle
	toggle() {
		this.active = !this.active;
		return this.active ? this.activate() : this.deactivate();
	}
}

class Tooltip {
	constructor(el, message) {
		this.el = el;
		this.message = message;
		this.tooltipEl = null;

		this.show = this.show.bind(this);
		this.hide = this.hide.bind(this);

		this.el.addEventListener("mouseenter", this.show);
		this.el.addEventListener("mouseleave", this.hide);

		this.render();
	}

	render() {
		const tooltip = document.createElement("div");
		tooltip.className = "devtool-tooltip";

		// add an icon
		const icon = document.createElement("i");
		icon.className = "devtool-tooltip__icon";
		icon.textContent = "i";

		tooltip.prepend(icon);

		const message = document.createElement("span");
		message.className = "devtool-tooltip__message";
		message.textContent = this.message;	

		tooltip.append(message);

		this.el.appendChild(tooltip);
		this.tooltipEl = tooltip;
	}

	show() {
		if (!this.tooltipEl) {
			this.create();
		}

		this.tooltipEl.classList.add("is-active");
	}

	hide() {
		if (this.tooltipEl) {
			this.tooltipEl.classList.remove("is-active");
		}
	}
}

// create a plugin to visual check the markup of the page to ensure header tags are used correctly and in sequential order
class HeaderTagChecker extends DevToolPlugin {
	constructor() {
		super();
	}

	get elements() {
		const main = document.querySelector('body > main');

		return Array.from(main.querySelectorAll("h1, h2, h3, h4, h5, h6"));
	}

	activate() {
		console.log("HeaderTagChecker activated");
		// add your activation logic (DOM overlays, CSS toggles, etc.)

		this.elements.forEach((el, index, arr) => {
			this.checkSequentality(el, index, arr);
		});
	}

	deactivate() {
		console.log("HeaderTagChecker deactivated");

		this.elements.forEach((el) => {
			el.classList.remove("devtool-header-issue");
		});
		// add your deactivation logic (remove DOM overlays, CSS toggles, etc.)
	}

	checkSequentality(el, index, arr) {
		if (index === 0) return; // skip first element

		const currentLevel = parseInt(el.tagName.substring(1));
		const prevEl = arr[index - 1];
		if (prevEl) {
			const prevLevel = parseInt(prevEl.tagName.substring(1));
			if (currentLevel > prevLevel + 1) {
				this.highlightIssue(el, `Header level skipped from H${prevLevel} to H${currentLevel}`);
			}
		}
	}

	highlightIssue(el, message) {
		el.classList.add("devtool-header-issue");

		// check if tooltip already exists
		const tooltip = el.querySelector('.devtool-tooltip');

		// if (!tooltip) {
			const tool = new Tooltip(el, message);
			console.log(tool)
		// }
	}
}
class SpacingVisualiser extends DevToolPlugin {
	constructor() {
		super();

		this.selectors = [
			'div[is="flex-row"] > .wrapper > .flex-inner',
			".syn-spacer",
			".heading-column",
		];
		this.overlays = new Map();

		console.log(this.elements);
	}

	get elements() {
		return this.selectors.flatMap((selector) =>
			Array.from(document.querySelectorAll(selector))
		);
	}

	createFlexWrapperOverlay(el) {
		const spacingTop = window.getComputedStyle(el).paddingTop;
		const spacingBottom = window.getComputedStyle(el).paddingBottom;

		if (
			(!spacingTop || spacingTop === "0px") &&
			(!spacingBottom || spacingBottom === "0px")
		)
			return null;

		const overlay = document.createElement("div");
		const coordsTop = document.createElement("div");
		const coordsBottom = document.createElement("div");

		overlay.className = "spacing-overlay";

		coordsTop.className = "spacing-coords spacing-coords--top";
		coordsTop.style.height = `${spacingTop}`;
		coordsTop.textContent = spacingTop;

		coordsBottom.className = "spacing-coords spacing-coords--bottom";
		coordsBottom.style.height = `${spacingBottom}`;
		coordsBottom.textContent = spacingBottom;

		overlay.appendChild(coordsTop);
		overlay.appendChild(coordsBottom);

		return overlay;
	}

	createSpacerOverlay(el) {
		const spacing = window.getComputedStyle(el).height;
		const overlay = document.createElement("div");
		const coords = document.createElement("div");

		overlay.className = "spacing-overlay";

		coords.className = "spacing-coords";
		coords.textContent = spacing;

		overlay.appendChild(coords);

		return overlay;
	}

	createOverlay(el) {
		let overlay = null;

		if (
			el.classList.contains("flex-inner") ||
			el.classList.contains("heading-column")
		) {
			overlay = this.createFlexWrapperOverlay(el);
		} else if (el.classList.contains("syn-spacer")) {
			overlay = this.createSpacerOverlay(el);
		}

		if (overlay) {
			el.style.position = "relative";
			el.appendChild(overlay);

			this.overlays.set(el, overlay);
		}
	}

	activate() {
		console.log("SpacingVisualiser activated");
		// add your activation logic (DOM overlays, CSS toggles, etc.)
		this.elements.forEach((el) => {
			el.classList.add("spacing-visualiser-active");

			if (!this.overlays.has(el)) {
				this.createOverlay(el);
			}
		});
	}

	deactivate() {
		console.log("SpacingVisualiser deactivated");

		this.elements.forEach((el) => {
			el.classList.remove("spacing-visualiser-active");
		});
	}
}

// DevTools manager
class DevTools {
	plugins = {};

	constructor() {
		console.log("DevTools initialized");

		// register an instance
		this.register("spacingVisualiser", new SpacingVisualiser());
		// this.register("headerTagChecker", new HeaderTagChecker());

		// optional: wire clicks on elements like <button data-devtool="spacingVisualiser">
		this._createSidebarUI();
		this._setupUIListeners();
	}

	register(name, instance) {
		if (!(instance instanceof DevToolPlugin)) {
			throw new Error("Registered plugin must extend DevToolPlugin");
		}
		this.plugins[name] = instance;
	}

	get(name) {
		return this.plugins[name];
	}

	toggle(name) {
		const p = this.get(name);
		if (!p) throw new Error(`Plugin "${name}" not found`);
		return p.toggle();
	}

	activate(name) {
		const p = this.get(name);
		if (!p) throw new Error(`Plugin "${name}" not found`);
		if (!p.active) p.activate();
	}

	deactivate(name) {
		const p = this.get(name);
		if (!p) throw new Error(`Plugin "${name}" not found`);
		if (p.active) p.deactivate();
	}

	_createSidebarUI() {
		const template = `
    <style>
    .devtools-sidebar {
		position: fixed;
		bottom: 8px;
		left: 8px;
		z-index: 99999;
		display: flex;
		flex-direction: column;
		gap: 10px;

        button {
          border-radius: 6px;
          background: rgb(45, 45, 47);
          border: 1px solid rgba(255, 255, 255, 0.07);
          padding: 8px 16px 8px 40px;
          margin: 0;
          cursor: pointer;
          font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif;
          color: #FFF;
          font-weight: 500;
          position: relative;

          &:before {
            content: "";
            position: absolute;
            top: 50%;
            left: 16px;
            transform: translateY(-50%) scale(0);
            transition: all 300ms ease;
            width: 16px;
            height: 16px;
            background: #3f71e0;
            border-radius: 50%;
            z-index: 3;
          }

          &:after {
            content: "";
            position: absolute;
            top: 50%;
            left: 16px;
            transform: translateY(-50%);
            width: 16px;
            height: 16px;
            background: #ffffff38;
            border-radius: 50%;
            z-index: 2;
          }

          &:hover,
          &.is-active {
            color: #fff;

            &:before {
              transform: translateY(-50%) scale(1);
            }
          }
        }
    }

    .spacing-visualiser-active {
      .spacing-overlay {
        display: block !important;
      }
    }

    .spacing-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        border: 1px dashed #FF0DB4;
        pointer-events: none;
        display: none;

        &.active {
            display: block;
        }
    }

    .spacing-coords {
        position: absolute;
        right: calc(50% - 50px);
        transform: translate(50%, 0);
        pointer-events: none;
        height: 100%;
        top: 0;
        width: 100px;
        padding: 0;
        font-size: 16px;
        text-indent: 16px;
        line-height: 50px;
        font-weight: 500;
        border-left: 2px solid #FF0DB4 !important;
        color: #FF0DB4 !important;
        display: flex;
        align-items: center;

        &:before {
            content: "";
            top: 0px;
            left: -11px;
            width: 22px;
            height: 2px;
            background: #FF0DB4 !important;
            position: absolute;
        }

        &:after {
            content: "";
            bottom: 0px;
            left: -11px;
            width: 22px;
            height: 2px;
            background: #FF0DB4 !important;
            position: absolute;
        }

        &.spacing-coords--bottom {
            top: auto;
            bottom: 0;
        }
    }

	.devtool-tooltip {
		position: absolute;
		top: -10px;
		right: -10px;

		&.is-active {
			span.devtool-tooltip__message {
			transform: scale(1) translate(-100%, -105%);
			opacity: 1;
			visibility: visible;
			transition: all 300ms ease;
			}
		}

		i.devtool-tooltip__icon {
			width: 18px;
			height: 18px;
			display: flex;
			border-radius: 50%;
			background: grey;
			font-size: 12px;
			justify-content: center;
			align-items: center;
			color: #fff;
			font-style: italic;
			box-shadow: 0 0 3px 3px #0000001f;
		}

		.devtool-tooltip__message {
			position: absolute;
			top: 0;
			left: 50%;
			padding: 10px;
			font-size: 14px;
			font-weight: 400;
			color: #000 !important;
			background: #fff;
			border-radius: 5px;
			box-shadow: 0 1px 8px rgba(0, 0, 0, 0.15);
			transition: opacity 300ms ease;
			transform: scale(0) translate(-50%, -100%);
			transform-origin: left;
			max-width: 400px;
			opacity: 0;
			min-width: 250px;
			text-align: center;
			visibility: hidden;
		}
	}

	.devtool-header-issue {
		border: 1px dashed orange !important;
		position: relative !important;
	}


    </style>
      <div class="devtools-sidebar">
        <button data-devtool="spacingVisualiser">Toggle Spacing Visualiser</button>
      </div>
    `;

		document.body.insertAdjacentHTML("beforeend", template);
	}

	_setupUIListeners() {
		document.addEventListener("click", (e) => {
			const btn = e.target.closest("[data-devtool]");
			if (!btn) return;
			btn.classList.toggle("is-active");
			const name = btn.dataset.devtool;
			this.toggle(name);
		});
	}
}

export { DevTools };
